import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { Copy, Check, Link2, Trash2, Share as ShareIcon } from 'lucide-react';
import { nativeShare, isNativePlatform } from '../lib/native';

export const ShareDialog = ({ open, onOpenChange, habit, userId }) => {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !habit) return;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('habit_shares')
        .select('*')
        .eq('habit_id', habit.id)
        .maybeSingle();
      setShare(data);
      setLoading(false);
    })();
  }, [open, habit]);

  const generate = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('habit_shares')
      .insert({ habit_id: habit.id, user_id: userId })
      .select()
      .maybeSingle();
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setShare(data);
    toast.success('Share link created');
  };

  const revoke = async () => {
    if (!share) return;
    if (!window.confirm('Revoke this share link? Anyone with the old link will lose access.')) return;
    setLoading(true);
    const { error } = await supabase.from('habit_shares').delete().eq('id', share.id);
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setShare(null);
    toast.success('Share link revoked');
  };

  const shareUrl = share ? `${process.env.REACT_APP_BACKEND_URL}/api/share/preview/${share.share_token}` : '';

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the text manually');
    }
  };

  const openNativeShare = async () => {
    const ok = await nativeShare({
      title: `${habit?.name} · My Flow Habit`,
      text: `Check out my ${habit?.name} flow on My Flow Habit.`,
      url: shareUrl,
    });
    if (!ok) copyUrl();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" /> Share your flow
          </DialogTitle>
          <DialogDescription>
            Create a public read-only link of <span className="text-foreground font-medium">{habit?.name}</span>&apos;s heatmap and streak. Anyone with the link can view (no account required). Your other habits stay private.
          </DialogDescription>
        </DialogHeader>

        {loading && !share ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : !share ? (
          <Button onClick={generate} disabled={loading} className="rounded-full" data-testid="share-generate">
            Create share link
          </Button>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly className="font-mono text-xs" data-testid="share-url" />
              {isNativePlatform() ? (
                <Button onClick={openNativeShare} className="rounded-full shrink-0" data-testid="share-native">
                  <ShareIcon className="h-4 w-4" /> Share
                </Button>
              ) : (
                <Button onClick={copyUrl} className="rounded-full shrink-0" data-testid="share-copy">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Created {new Date(share.created_at).toLocaleDateString()}. Tip: pin this to your bio or send it to an accountability buddy.
            </p>
            <Button onClick={revoke} variant="ghost" className="rounded-full text-destructive hover:text-destructive" data-testid="share-revoke">
              <Trash2 className="h-4 w-4" /> Revoke link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
